<template>
  <div>
    <!-- Widgets -->
    <div class="overview-container">
      <div class="overview-item"
           v-for="(item, index) in overviewData"
           :key="index"
           :class="{ 'red-widget': isRed(item) }"
           @click="toggleDetails(item)">
        <i class="material-icons">{{ item.icon }}</i>
        <div class="overview-text">
          <span class="overview-title">{{ item.title }}</span>
          <span class="overview-subtitle">{{ item.subtitle }}</span>
        </div>
      </div>
    </div>

    <!-- Widget Details -->
    <div class="details-container" v-if="selectedItem">

      <!-- Sites Monitored -->
      <div v-if="selectedItem.key === 'Sitesmonitored'" class="sites-details">
        <h6>{{ selectedItem.title }}</h6>
        <p>{{ $t('dashboard.nonotifications') }}.</p>
      </div>

      <!-- Assets Monitored -->
      <div v-if="selectedItem.key === 'AssetMonitored'" class="assets-details">
        <h6>{{ selectedItem.title }}</h6>
        <p>{{ $t('dashboard.nonotifications') }}.</p>
      </div>

      <!-- Channels Monitored -->
      <div v-if="selectedItem.key === 'ChannelsMonitored'" class="channels-details">
        <h6>{{ selectedItem.title }}</h6>
        <div v-if="widgetData['MicroPOSSChannelsWithError'].length || widgetData['ChannelErrors'].length">
          <h6>{{ $t('dashboard.channelerrors') }}:</h6>
          <ul>
            <li v-for="(error, idx) in widgetData['ChannelErrors']" :key="idx">{{ error }}</li>
          </ul>
        </div>
        <p v-else-if="!widgetData['MicroPOSSChannelsWithError'].length && !widgetData['ChannelErrors'].length">{{ $t('dashboard.nonotifications') }}.</p>
      </div>

      <!-- Total MicroPOSS -->
      <div v-if="selectedItem.key === 'TotalMicroPOSS'" class="loggers-details">
        <h6>{{ selectedItem.title }}</h6>
        <div v-if="widgetData['MissingMicroPoss'].length" v-b-tooltip.hover.top :title="$t('dashboard.lateormissing')">
          <h6>{{ $t('dashboard.missingdataloggers') }}</h6>
          <ul>
            <li v-for="(missing, idx) in widgetData['MissingMicroPoss']" :key="idx">{{ missing }}</li>
          </ul>
        </div>
        <div v-if="widgetData['LateMicroPOSS'].length" v-b-tooltip.hover.top :title="$t('dashboard.lateormissing')">
          <h6>{{ $t('dashboard.latedataloggers') }}:</h6>
          <ul>
            <li v-for="(late, idx) in widgetData['LateMicroPOSS']" :key="idx">{{ late }}</li>
          </ul>
        </div>
        <p v-else-if="!widgetData['MissingMicroPoss'].length && !widgetData['LateMicroPOSS'].length">{{ $t('dashboard.nonotifications') }}.</p>
      </div>

      <!-- Status Not OK (Alarms) -->
      <div v-if="selectedItem.key === 'StatusNotOk'" class="alarms-details">
        <h6>{{ selectedItem.title }}</h6>
        <div v-if="detailsData.length">
          <h6>{{ $t('dashboard.havealarmsfor') }}:</h6>
          <ul>
            <li v-for="(detail, idx) in detailsData" :key="idx">
              {{ detail }}
            </li>
          </ul>
        </div>
        <p v-else>{{ $t('dashboard.nonotifications') }}.</p>
      </div>

      <!-- In Maintenance -->
      <div v-if="selectedItem.key === 'InMaintenance'" class="maintenance-details">
        <h6>{{ selectedItem.title }}</h6>
        <div v-if="widgetData[selectedItem.key].length">
          <h6>{{ $t('dashboard.inmaintenance') }}:</h6>
          <ul>
            <li v-for="(item, idx) in widgetData[selectedItem.key]" :key="idx">{{ item }}</li>
          </ul>
        </div>
        <p v-else>{{ $t('dashboard.nonotifications') }}.</p>
      </div>
    </div>
  </div>
</template>

<script>
import AuthenticationService from '@/services/AuthenticationService'
import DashboardService from '@/services/DashboardService'

export default {
  name: 'Overview',
  props: ['companyId', 'updated'],
  data () {
    return {
      currentUser: AuthenticationService.getUser(),
      errorMessage: null,
      widgetData: {},
      selectedItem: null,
      detailsData: [],
      overviewData: [
        { key: 'Sitesmonitored', icon: 'location_on', title: this.$t('dashboard.sites'), subtitle: this.$t('loading') + '...' },
        { key: 'AssetMonitored', icon: 'label_outline', title: this.$t('views.navbar.asset'), subtitle: this.$t('loading') + '...' },
        { key: 'ChannelsMonitored', icon: 'call_split', title: this.$t('dashboard.channels'), subtitle: this.$t('loading') + '...' },
        { key: 'TotalMicroPOSS', icon: 'input', title: this.$t('dashboard.loggers'), subtitle: this.$t('loading') + '...' },
        { key: 'StatusNotOk', icon: 'warning', title: this.$t('dashboard.alarms'), subtitle: this.$t('loading') + '...', detailsKey: 'HaveAlarmsFor' },
        { key: 'InMaintenance', icon: 'engineering', title: this.$t('maintenance'), subtitle: this.$t('loading') + '...' }
      ]
    }
  },
  watch: {
    companyId: {
      immediate: true,
      async handler (newCompanyId) {
        if (newCompanyId) {
          await this.fetchDashboardWidgets(newCompanyId)
        }
      }
    },
    updated: {
      async handler () {
        for (const d of this.overviewData) {
          d.subtitle = this.$t('loading')
        }
        await this.fetchDashboardWidgets(this.companyId)
      }
    },
    '$i18n.locale': {
      immediate: true,
      handler (newLang, oldLang) {
        if (oldLang !== undefined) {
          this.refreshTranslations()
        }
      }
    }
  },
  methods: {
    async fetchDashboardWidgets (companyId) {
      this.errorMessage = null
      try {
        const response = await DashboardService.getDashboardWidgets(this.currentUser.UserId, companyId)
        this.widgetData = response.data
        this.processWidgetData()
      } catch (error) {
        this.errorMessage = error.message || 'Failed to load data.'
        this.widgetData = {}
      }
    },

    processWidgetData () {
      if (!this.widgetData) return

      this.overviewData = this.overviewData.map(item => {
        let value = this.widgetData[item.key]
        if (Array.isArray(value)) {
          value = value.length
        }
        return {
          ...item,
          subtitle: `${value} ${item.title}`
        }
      })
    },

    toggleDetails (item) {
      if (this.selectedItem && this.selectedItem.key === item.key) {
        this.selectedItem = null // Collapse if same item clicked
        this.detailsData = []
      } else {
        this.selectedItem = item
        this.detailsData = this.widgetData[item.detailsKey] || []
      }
    },

    visitLocation (railObjectId) {
      this.$router.push(`/switch/${railObjectId}`)
    },

    refreshTranslations () {
      this.overviewData = this.overviewData.map(item => {
        const newTitle = this.$t(
          item.key === 'Sitesmonitored' ? 'dashboard.sites'
          : item.key === 'AssetMonitored' ? 'views.navbar.asset'
          : item.key === 'ChannelsMonitored' ? 'dashboard.channels'
          : item.key === 'TotalMicroPOSS' ? 'dashboard.loggers'
          : item.key === 'StatusNotOk' ? 'dashboard.alarms'
          : 'maintenance'
        )
        // Extract the numeric value from the current subtitle to translate, or use loading
        const currentSubtitle = item.subtitle || ''
        const valueMatch = currentSubtitle.match(/^(\d+)/) // Extract leading number
        const newSubtitle = currentSubtitle.includes(this.$t('loading'))
          ? this.$t('loading') + '...'
          : `${valueMatch ? valueMatch[1] : ''} ${newTitle}`

        return {
          ...item,
          title: newTitle,
          subtitle: newSubtitle
        }
      })
    },

    isRed (item) {
      if (item.key === 'ChannelsMonitored' && this.widgetData['MicroPOSSChannelsWithError']?.length > 0) {
        return true
      }
      if (item.key === 'TotalMicroPOSS' && this.widgetData['LateMicroPOSS']?.length > 0) {
        return true
      }
      if (item.key === 'StatusNotOk' && this.widgetData['HaveAlarmsFor']?.length > 0) {
        return true
      }
      return false
    }
  }
}
</script>

<style scoped>
/* Overview Container */
.overview-container {
  background: white;
  height: 100px;
  width: 100%;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Each Item */
.overview-item {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 255, 0, 0.2);
  padding: 10px;
  border-radius: 10px;
  border: 2px solid #BDBDBD;
  width: 180px;
  height: 70px;
  text-align: left;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s, background 0.2s;
}

.overview-item:hover {
  transform: scale(1.05);
  background: rgba(0, 255, 0, 0.432);
}

.red-widget {
  background: rgba(255, 0, 0, 0.2);
  border-color: #F44336;
}

.red-widget:hover {
  background: rgba(255, 0, 0, 0.432);
}

/* Icon Styling */
.material-icons {
  font-size: 50px;
  color: #424242;
  margin-right: 10px;
}

/* Text Styling */
.overview-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
}

.overview-title {
  font-weight: bold;
  font-size: 16px;
  white-space: nowrap;
}

.overview-subtitle {
  font-size: 14px;
  color: #616161;
  white-space: nowrap;
}

/* Base Details Container */
.details-container {
  background: white;
  padding: 15px;
  margin-top: 10px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Specific Details Styling */

/* Sites Monitored */
.sites-details {
  text-align: center;
  background: white;
  padding: 20px;
}

.sites-details h6 {
  color: black;
  margin-bottom: 10px;
}

.sites-details p {
  font-size: 16px;
  color: black;
}

/* Assets Monitored */
.assets-details {
  text-align: center;
  background: white;
  padding: 20px;
}

.assets-details h6 {
  color: black;
  margin-bottom: 10px;
}

.assets-details p {
  font-size: 16px;
  color: black;
}

/* Channels Monitored */
.channels-details {
  text-align: center;
  background: white;
  padding: 20px;
}

.channels-details h6 {
  color: black;
  margin-bottom: 10px;
}

.channels-details p {
  font-size: 16px;
  color: black;
}

/* Total MicroPOSS */
.loggers-details {
  text-align: center;
  background: white;
  padding: 20px;
}

.loggers-details h6 {
  color: black;
  margin-bottom: 10px;
}

.loggers-details p {
  font-size: 16px;
  color: black;
}

/* Status Not OK (Alarms) */
.alarms-details {
  padding: 20px;
  background: white;
}

.alarms-details h6 {
  color: black;
  text-align: center;
  margin-bottom: 15px;
}

.alarms-details ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
  width: 100%;
  text-align: center;
}

.alarms-details ul li {
  border-radius: 5px;
  transition: background 0.3s ease;
}

.alarms-details p {
  text-align: center;
  color: black;
  font-size: 16px;
}

/* In Maintenance */
.maintenance-details {
  text-align: center;
  background: white;
  padding: 20px;
}

.maintenance-details h6 {
  color: black;
  margin-bottom: 10px;
}

.maintenance-details p {
  font-size: 16px;
  color: black;
}

.tooltip {
  position: relative;
  display: inline-block;
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  border-bottom: 1px dotted black;
}
</style>



// WEBPACK FOOTER //
// src/pages/dashboard3/_Overview.vue